import React from 'react';
import { motion } from 'framer-motion';
import { 
  GitMerge, 
  PenTool, 
  Settings2, 
  FileCheck2, 
  Shield 
} from 'lucide-react';

const TrustAndGuarantees = () => {
  const cards = [
    {
      title: "Méthodologie structurée",
      desc: "Analyse approfondie, audit technique rigoureux, restitution détaillée et accompagnement personnalisé tout au long du processus.",
      icon: GitMerge, // Correspond au symbole de structure/workflow
      pos: "md:-translate-x-10 md:-translate-y-4"
    },
    {
      title: "Confidentialité garantie",
      desc: "Chaque projet est encadré par un accord de non-divulgation (NDA) strict pour assurer la protection de vos données sensibles.",
      icon: PenTool, // Correspond au symbole de signature/NDA
      pos: "md:translate-x-10 md:-translate-y-4"
    },
    {
      title: "Outils professionnels",
      desc: "Utilisation exclusive d'outils reconnus mondialement par les experts (OSINT, scans de vulnérabilités, tests d'intrusion).",
      icon: Settings2, // Correspond à l'icône de réglages/outils
      pos: "md:-translate-x-10 md:translate-y-4"
    },
    {
      title: "Recommandations actionnables",
      desc: "Un rapport clair, dénué de jargon inutile, avec des recommandations concrètes et priorisées pour renforcer votre posture.",
      icon: FileCheck2, // Correspond au document avec check
      pos: "md:translate-x-10 md:translate-y-4"
    }
  ];

  return (
    <section className="w-full bg-[#F6FAFD] py-16 px-6 md:py-24 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-16 md:mb-24">
          <motion.span 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-[#3b82f6] text-[12px] font-bold uppercase tracking-[0.2em] bg-[#3b82f6]/10 px-4 py-1.5 rounded-full"
          >
            SÉCURITÉ & FIABILITÉ
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-[#0A1931] text-[32px] md:text-[48px] font-black mt-6 mb-4"
          >
            Confiance & garanties
          </motion.h2>
          <p className="text-[#1A3D63]/70 text-sm md:text-base max-w-2xl mx-auto font-medium">
            Votre sécurité est notre priorité absolue. Nous nous engageons sur la qualité irréprochable et la confidentialité totale.
          </p>
        </div>

        {/* Animation Container */}
        <div className="relative flex flex-col items-center">
          
          {/* Cercle Central avec Bouclier */}
          <div className="hidden md:flex absolute inset-0 items-center justify-center z-10 pointer-events-none">
            <motion.div 
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ type: "spring", damping: 12 }}
              className="w-32 h-32 bg-[#3b82f6]/5 rounded-full flex items-center justify-center border border-[#3b82f6]/10 shadow-inner"
            >
              <div className="w-16 h-16 bg-white rounded-full shadow-xl flex items-center justify-center text-[#3b82f6] border border-[#E5EDF5]">
                <Shield size={32} fill="currentColor" fillOpacity={0.1} />
              </div>
            </motion.div>
          </div>

          {/* Grille des Cartes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-16 md:gap-y-8 w-full">
            {cards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className={`bg-white p-8 rounded-[1.5rem] border border-[#E5EDF5] shadow-sm hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 group ${card.pos}`}
              >
                <div className="flex flex-col items-start md:group-odd:items-end md:group-odd:text-right">
                  <div className="w-12 h-12 bg-[#3b82f6]/5 text-[#3b82f6] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#3b82f6] group-hover:text-white transition-colors duration-300">
                    <card.icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-[#0A1931] text-xl font-black mb-3">{card.title}</h3>
                  <p className="text-[#1A3D63]/70 text-sm leading-relaxed font-medium">
                    {card.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-20 pt-12 border-t border-[#E5EDF5] grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { val: "150+", label: "PROJETS SÉCURISÉS" },
            { val: "100%", label: "SATISFACTION CLIENT" },
            { val: "12", label: "EXPERTS CERTIFIÉS" },
            { val: "24/7", label: "ACCOMPAGNEMENT" }
          ].map((stat, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="text-center"
            >
              <div className="text-[#3b82f6] text-3xl md:text-4xl font-black mb-1">{stat.val}</div>
              <div className="text-[#1A3D63]/50 text-[10px] font-black uppercase tracking-widest">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustAndGuarantees;